import React from 'react';
import { Inbox, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FormBuilderState, FormField } from '@/components/form-builder/FormBuilder';

interface FormPreviewProps {
  formState: FormBuilderState;
  mode: 'desktop' | 'tablet' | 'mobile';
}

const FormPreview: React.FC<FormPreviewProps> = ({ formState, mode }) => {
  const { t } = useTranslation('common');
  const containerWidth = {
    desktop: '760px',
    tablet: '768px',
    mobile: '390px',
  }[mode];

  const renderField = (field: FormField) => {
    const baseInputClass =
      'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 transition focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-gray-900 dark:text-white';

    switch (field.fieldType) {
      case 'textarea':
        return <textarea className={baseInputClass} placeholder={field.fieldPlaceholder} required={field.required} rows={4} />;
      case 'select':
        return (
          <select className={baseInputClass} required={field.required}>
            <option value="">{field.fieldPlaceholder || t('formBuilder.canvas.selectOption', { defaultValue: 'Select an option' })}</option>
            {field.options?.map((opt, idx) => <option key={idx} value={opt}>{opt}</option>)}
          </select>
        );
      case 'checkbox':
        return (
          <div className="space-y-3">
            {field.options?.map((opt, idx) => (
              <label key={idx} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-gray-700 dark:text-gray-300">
                <input type="checkbox" value={opt} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        );
      case 'radio':
        return (
          <div className="space-y-3">
            {field.options?.map((opt, idx) => (
              <label key={idx} className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-gray-700 dark:text-gray-300">
                <input type="radio" name={field.fieldId} value={opt} required={field.required} />
                <span>{opt}</span>
              </label>
            ))}
          </div>
        );
      default:
        return (
          <input
            type={field.fieldType}
            className={baseInputClass}
            placeholder={field.fieldPlaceholder}
            defaultValue={field.defaultValue}
            required={field.required}
            min={field.validation?.min}
            max={field.validation?.max}
            pattern={field.validation?.pattern}
          />
        );
    }
  };

  const primaryColor = formState.theme.primaryColor || '#e20074';
  const secondaryColor = formState.theme.secondaryColor || '#7f123f';
  const fontFamily = formState.theme.fontFamily || 'inherit';

  return (
    <div className="flex h-full flex-col" style={{ fontFamily }}>
      <div className="flex-1 overflow-y-auto bg-[#fcf0f6] p-6 dark:bg-[#171016]">
        <div className="flex min-h-full items-start justify-center">
          <div style={{ width: containerWidth }} className="transition-all duration-300">
            <div className="overflow-hidden rounded-[28px] border border-[#e9e5eb] bg-white shadow-[0_30px_120px_-60px_rgba(226,0,116,0.35)] dark:border-[#3d2b36] dark:bg-[#171016]">
              <div className="h-4" style={{ background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})` }} />

              <div className="border-b border-slate-100 p-8 dark:border-gray-800">
                <div className="mb-7 flex items-center justify-between gap-4">
                  <img
                    src="/t-systems.svg"
                    alt="T-SYSTEMS"
                    className="h-6 w-auto shrink-0"
                    draggable={false}
                  />
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-600 dark:text-brand-400">
                    AIDA Forms
                  </span>
                </div>
                <h1 className="break-words text-[clamp(1.25rem,3.6vw,1.875rem)] font-semibold leading-tight text-slate-950 dark:text-white">
                  {formState.formTitle || t('formBuilder.defaults.formTitle', { defaultValue: 'Untitled form' })}
                </h1>
                <p className="mt-3 text-sm leading-7 text-slate-500 dark:text-gray-400">
                  {formState.formDescription || t('formBuilder.preview.descriptionFallback', { defaultValue: 'Add a short description to explain the purpose of this form.' })}
                </p>
              </div>

              <div className="p-5 sm:p-8">
                {formState.config.showProgressBar && formState.fields.length > 0 && (
                  <div className="mb-6">
                    <div className="mb-2 flex items-center justify-between text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400 dark:text-gray-500">
                      <span>{t('formBuilder.preview.progress', { defaultValue: 'Completion' })}</span>
                      <span>0%</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-gray-800">
                      <div className="h-full rounded-full" style={{ width: '0%', backgroundColor: primaryColor }} />
                    </div>
                  </div>
                )}

                {formState.fields.length === 0 ? (
                  <div className="py-16 text-center text-slate-300 dark:text-slate-600">
                    <Inbox className="mx-auto mb-3 h-10 w-10" />
                    <p className="text-sm">
                      {t('formBuilder.preview.empty', { defaultValue: 'Add questions to preview your form.' })}
                    </p>
                  </div>
                ) : (
                  <form className="space-y-5">
                    {formState.fields.map((field) => (
                      <section key={field.id} className="rounded-[24px] border border-slate-200 bg-slate-50 p-5 dark:border-gray-800 dark:bg-gray-900">
                        <label className="mb-3 block text-sm font-medium text-slate-900 dark:text-white">
                          {field.fieldLabel}
                          {field.required && <span className="ml-1 text-red-500">*</span>}
                        </label>
                        {renderField(field)}
                        {field.validation?.message && (
                          <p className="mt-2 text-xs text-slate-400 dark:text-gray-500">{field.validation.message}</p>
                        )}
                      </section>
                    ))}

                    <div className="pt-4">
                      <button
                        type="submit"
                        style={{ background: primaryColor }}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-3 text-sm font-medium text-white transition hover:opacity-90"
                      >
                        <Send className="h-4 w-4" />
                        {formState.submitButtonText || t('formBuilder.defaults.submitButton', { defaultValue: 'Submit' })}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">{formState.fields.length}</div>
            <div className="text-[11px] text-slate-500 dark:text-gray-400">
              {t('formBuilder.preview.stats.fields', { defaultValue: 'Fields' })}
            </div>
          </div>
          <div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">
              {formState.fields.filter((f) => f.required).length}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-gray-400">
              {t('formBuilder.preview.stats.required', { defaultValue: 'Required' })}
            </div>
          </div>
          <div>
            <div className="text-lg font-bold text-slate-900 dark:text-white">
              {formState.fields.filter((f) => f.conditional).length}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-gray-400">
              {t('formBuilder.preview.stats.conditional', { defaultValue: 'Conditional' })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormPreview;
